/* eslint-disable react-hooks/exhaustive-deps */

import { useCallback, useState, useEffect } from 'react';
import {
  values,
  debounce,
  isArray,
  isEmpty,
} from 'lodash';
import { toast } from '../utilities';

/**
 * @param filtersFixtures
 * @description each filter should have a distinct name
 */

const DELAY_IN_MS = 1000;


export default function useFilter(filtersFixtures, filtersType, namespace) {
  const [state, setState] = useState(JSON.parse(localStorage.getItem(filtersType)) || {});
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    const initStateWithDefaultValue = () => {
      if (!i18next.hasLoadedNamespace(namespace)) return;
      let updatedState = { ...state };
      let shouldUpdate = false;
      filtersFixtures.forEach((filters) => {
        if (!filters.options) return;
        let filterType;
        switch (filters.type) {
          case 'checkbox':
            filterType = 'checked';
            break;
          case 'input':
            filterType = 'inputValue';
            break;
          case 'datePicker':
            filterType = 'datePicker';
            break;
          case 'dropdown/multiple':
            // TODO implement default value logic here, if we will need it
            break;
          default: break;
        }

        if (!filterType) return;
        filters.options.forEach((filter) => {
          if ('defaultValue' in filter) {
            shouldUpdate = true;
            updatedState = { ...updatedState, [filter.name]: { ...filter, [filterType]: filter.defaultValue } };
          }
        });
      });

      if (shouldUpdate) setState(updatedState);
    };

    initStateWithDefaultValue();
  }, [i18next.hasLoadedNamespace(namespace)]); // TODO remove condition, when problem with translations will be solved

  useEffect(() => {
    const setStateToLocalStorage = () => {
      try {
        localStorage.setItem(filtersType, JSON.stringify(state));
      } catch (error) {
        toast.error(error.message || error);
      }
    };

    window.addEventListener('beforeunload', setStateToLocalStorage);

    return () => {
      setStateToLocalStorage();
      window.removeEventListener('beforeunload', setStateToLocalStorage);
    };
  }, [state]);

  function toggleFilter(filter) {
    const updatedState = { ...state, [filter.name]: { ...filter, checked: !state[filter.name]?.checked } };
    setState(updatedState);
  }

  function toggleFilterGroup(group) {
    const updatedState = { ...state };
    const filters = getGroupFilters(group);

    if (updatedState[createAllGroupName(group)]?.checked) {
      filters.options.forEach((filter) => {
        updatedState[filter.name] = { ...filter, checked: false };
      });

      updatedState[createAllGroupName(group)] = { checked: false };
    } else {
      filters.options.forEach((filter) => {
        updatedState[filter.name] = { ...filter, checked: true };
      });

      updatedState[createAllGroupName(group)] = { checked: true };
    }

    setState(updatedState);
  }

  function getGroupFilters(group) {
    return filtersFixtures.find(({ group: filterGroup }) => filterGroup === group) || [];
  }

  function createAllGroupName(groupLabel) {
    return `${groupLabel}All`.trim();
  }

  const handleInputChange = useCallback(debounce((filter, value) => {
    const updatedState = { ...state, [filter.name]: { ...filter, inputValue: value } };
    setState(updatedState);
  }, DELAY_IN_MS), [getFilledInputs()]);

  const handleDatePicker = useCallback(debounce((filter, value) => {
    const updatedState = { ...state, [filter.name]: { ...filter, inputValue: value } };
    setState(updatedState);
  }, DELAY_IN_MS), [getFilledInputs()]);

  const clearInputSearch = (filter) => {
    const updatedState = { ...state, [filter.name]: { ...filter, inputValue: '' } };
    setState(updatedState);
  };

  const handleChangeMultipleDropdown = (key, value) => {
    const updatedState = { ...state, [key]: value };
    setState(updatedState);
  };

  function uncheckFilter(filter) {
    const updatedState = { ...state, [filter.name]: { ...filter, checked: false } };
    setState(updatedState);
  }

  function getSelectedFilters() {
    return values(state).filter(filter => filter?.checked && filter.label);
  }

  function getFilledInputs() {
    return values(state).filter(filter => filter?.inputValue);
  }

  function getSelectedDatePickers() {
    const dates = {};
    if (state.createFromDate) dates[state.createFromDate.name] = state.createFromDate.inputValue;
    if (state.createToDate) dates[state.createToDate.name] = state.createToDate.inputValue;
    if (state.updateFromDate) dates[state.updateFromDate.name] = state.updateFromDate.inputValue;
    if (state.updateToDate) dates[state.updateToDate.name] = state.updateToDate.inputValue;
    if (state.lastLoggedInFromDate) dates[state.lastLoggedInFromDate.name] = state.lastLoggedInFromDate.inputValue;
    if (state.lastLoggedInToDate) dates[state.lastLoggedInToDate.name] = state.lastLoggedInToDate.inputValue;
    return dates;
  }

  function checkAnyFiltersSelected() {
    const filters = values(state);
    const selectedFilters = filters.filter(filter => filter?.checked || filter?.inputValue);
    const selectedDropdowns = filters.filter(filter => Array.isArray(filter)).flat();

    return selectedFilters.length > 0 || selectedDropdowns.length > 0;
  }

  function getSelectedDropdownOptions() {
    return Object.entries(state).filter(([, setOfSelectedOptions]) => isArray(setOfSelectedOptions) && !isEmpty(setOfSelectedOptions));
  }

  function uncheckDropdownOption(dropdownName, optionToUncheck) {
    const updatedState = { ...state, [dropdownName]: state[dropdownName].filter(option => option.value !== optionToUncheck.value) };
    setState(updatedState);
  }

  function clearAllFilters() {
    setState({});
    localStorage.removeItem(filtersType); // TODO test after merge ST-1752
  }

  return {
    state,
    filtersFixtures,
    handlers: {
      toggleFilter,
      toggleFilterGroup,
      handleInputChange,
      createAllGroupName,
      handleChangeMultipleDropdown,
      clearAllFilters,
      checkAnyFiltersSelected,
      handleDatePicker,
    },
    uncheckFilter,
    getSelectedFilters,
    getFilledInputs,
    getSelectedDropdownOptions,
    uncheckDropdownOption,
    isFilterOpen,
    setIsFilterOpen,
    clearInputSearch,
    getSelectedDatePickers,
  };
}
